import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, BadgeCheck, ArrowLeft, Clock } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import { BookingWidget } from "@/components/BookingWidget";
import { ReviewsSection } from "@/components/ReviewsSection";

export default function PractitionerProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/practitioners/${id}`).then((r) => setP(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="py-32 text-center text-muted-foreground">Practitioner not found.</div>;
  if (!p) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="practitioner-profile-page">
      <SEO title={`${p.business_name} — ${p.subcategory_name || p.category_name} in ${p.city_name}`}
        description={(p.bio || "").slice(0, 155)} />
      <Link to="/browse" className="text-sm text-muted-foreground flex items-center gap-1.5 mb-8 hover:text-foreground">
        <ArrowLeft size={14} /> Back to explore
      </Link>
      {p.is_preview && (
        <div className="rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 mb-8 text-sm text-foreground" data-testid="profile-preview-banner">
          Preview — this is how your page will look. It becomes visible to the community once the Team approves it and your listing is active.
        </div>
      )}
      <div className="grid md:grid-cols-[320px_1fr] gap-10">
        <div>
          <div className="aspect-[4/5] rounded-lg overflow-hidden silk-card">
            {p.photo ? <img src={assetUrl(p.photo)} alt={p.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center font-display text-6xl text-muted-foreground">{p.name?.[0]}</div>}
          </div>
          {p.business_card && (
            <a href={assetUrl(p.business_card)} target="_blank" rel="noreferrer" className="block mt-4 rounded-lg overflow-hidden silk-card" data-testid="practitioner-business-card">
              <img src={assetUrl(p.business_card)} alt={`${p.business_name} business card`} className="w-full object-cover" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground px-3 py-2">Business card · tap to enlarge</p>
            </a>
          )}
          <div className="mt-6">
            <BookingWidget practitioner={p} />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <h1 className="font-display text-4xl sm:text-5xl text-foreground" data-testid="practitioner-name">{p.business_name}</h1>
            {p.verified && <BadgeCheck size={22} className="text-primary" />}
          </div>
          <p className="text-muted-foreground mb-4">{p.name}</p>
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="practitioner-categories">
            {(p.category_names?.length ? p.category_names : [p.category_name]).map((n) => <Badge key={n} variant="secondary" className="font-normal">{n}</Badge>)}
            {p.subcategory_name && <Badge variant="outline" className="font-normal border-primary/40 text-primary">{p.subcategory_name}</Badge>}
            <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} />{p.city_name}, {p.country}</span>
          </div>
          <p className="text-foreground/85 leading-relaxed mb-10 whitespace-pre-wrap">{p.bio}</p>

          <h2 className="font-display text-lg md:text-2xl text-foreground mb-4">Services & pricing</h2>
          <div className="space-y-3 mb-8" data-testid="practitioner-services">
            {p.services?.map((s) => (
              <div key={s.id} className="silk-card rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-foreground">{s.name}</p>
                  {s.duration && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Clock size={11} />{s.duration}</p>}
                </div>
                <p className="font-display text-xl text-primary">${s.price} <span className="text-xs text-muted-foreground">CAD</span></p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-primary/25 bg-primary/5 p-4 mb-6">
            <p className="text-sm text-foreground/90">Pick a service and time to book instantly with secure Stripe payment. Questions first? Reach out at <a href={`mailto:${p.email}`} className="cream-link">{p.email}</a>{p.phone ? ` or ${p.phone}` : ""}.</p>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed" data-testid="practitioner-disclaimer">{p.disclaimer}</p>

          <ReviewsSection practitionerId={p.id} />
        </div>
      </div>
    </div>
  );
}
